import React from 'react';
import { useNavigate } from 'react-router-dom';
import { formatLargeNumber } from '../utils/formatNumber';
import { getCurrencySymbol } from '../utils/getCurrencySymbol';

const GlobalSearchModal = ({ show, onHide, results, loading, query, expandedWidth, pagination }) => {
  const navigate = useNavigate();

  const handleResultClick = (result) => {
    if (result.type === 'asset') {
      navigate(`/my-assets/${result.id}`);
    } else if (result.type === 'liability') {
      navigate(`/my-liability/${result.id}`);
    } else if (result.type === 'reminder') {
      navigate('/reminder');
    }
    onHide();
  };

  const formatAssetType = (assetType) => {
    if (!assetType) return '';
    return assetType.replace(/([A-Z])/g, ' $1').trim()
      .split(' ').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ');
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    return new Date(dateString).toLocaleDateString('en-US', { 
      year: 'numeric', 
      month: 'short', 
      day: 'numeric' 
    });
  };

  if (!show) return null;

  return (
    <div 
      className="dash-card"
      style={{
        position: 'absolute',
        top: 'calc(100% + 15px)',
        left: 0,
        width: expandedWidth || '400px',
        zIndex: 1050,
        maxHeight: '500px',
        overflowY: 'auto',
        background: 'rgba(255, 255, 255, 0.9)',
        backdropFilter: 'blur(10px)',
        border: '1px solid rgba(255, 255, 255, 0.3)',
        boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)'
      }}
    >
      <div className="dash-card-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div>
          <strong style={{ color: '#2B3674' }}>Search Results {query && `for "${query}"`}</strong>
          {pagination && (
            <div style={{ fontSize: '12px', color: '#707EAE', marginTop: '4px' }}>
              Showing {results.length} of {pagination.total} results
            </div>
          )}
        </div>
        <button 
          onClick={onHide}
          style={{
            border: 'none',
            background: 'none',
            fontSize: '18px',
            cursor: 'pointer',
            padding: '0',
            lineHeight: '1',
            width: '20px',
            height: '20px',
            color: '#2B3674',
            position: 'static',
            transform: 'none'
          }}
        >
          ×
        </button>
      </div>
      
      {loading ? (
        <div className="text-center py-4">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
        </div>
      ) : results.length === 0 ? (
        <div className="text-center py-4 text-muted">No results found</div>
      ) : (
        <div style={{ padding: '8px' }}>
          {results.map((result) => (
            <div
              key={result.id}
              onClick={() => handleResultClick(result)}
              style={{
                background: 'white',
                padding: '16px',
                borderRadius: '10px',
                border: '0',
                marginBottom: '8px',
                cursor: 'pointer',
                boxShadow: '0 2px 6px rgba(0, 0, 0, 0.05)',
                transition: 'all 0.2s ease'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.boxShadow = '0 4px 12px rgba(0, 0, 0, 0.1)';
                e.currentTarget.style.transform = 'translateY(-2px)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.boxShadow = '0 2px 6px rgba(0, 0, 0, 0.05)';
                e.currentTarget.style.transform = 'translateY(0)';
              }}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '12px' }}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '6px' }}>
                    <span className={`badge bg-${result.type === 'asset' ? 'success' : result.type === 'reminder' ? 'info' : 'warning'}`} style={{
                      fontSize: '10px',
                      padding: '3px 8px',
                      borderRadius: '12px',
                      textTransform: 'capitalize'
                    }}>
                      {result.type}
                    </span>
                    {result.status && (
                      <span style={{
                        fontSize: '10px',
                        padding: '3px 8px',
                        borderRadius: '12px',
                        background: result.status === 'active' ? '#E8F5E9' : '#FFF3E0',
                        color: result.status === 'active' ? '#2E7D32' : '#E65100',
                        textTransform: 'capitalize'
                      }}>
                        {result.status}
                      </span>
                    )}
                  </div>
                  <div style={{ fontWeight: '600', fontSize: '14px', color: '#2B3674', marginBottom: '4px' }}>
                    {result.name || result.description}
                  </div>
                  <div style={{ fontSize: '12px', color: '#707EAE', display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                    {result.assetType && (
                      <span>{formatAssetType(result.assetType)}</span>
                    )}
                    {result.category && (
                      <span>• {result.category}</span>
                    )}
                    {result.reminderType && (
                      <span>• {formatAssetType(result.reminderType)}</span>
                    )}
                    {result.date && (
                      <span>• {formatDate(result.date)}</span>
                    )}
                  </div>
                </div>
                {result.value && (
                  <div style={{ fontWeight: '600', fontSize: '14px', color: '#2B3674', whiteSpace: 'nowrap' }}>
                    {getCurrencySymbol(result.currency)} {formatLargeNumber(result.value, result.currency)}
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default GlobalSearchModal;